import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';
import Confetti from './Confetti';

const API        = process.env.REACT_APP_API_URL || '/api';
const SERVER_URL = API.replace(/\/api\/?$/, '');   // strip /api suffix for static file URLs
const ABLY_AUTH_URL = `${API}/ably/token`;

// ─── Shared Ably client (same pattern as TVDisplay) ───────────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
  return _ablyClient;
}

function useAblyChannel(channelName, handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const subs = Object.keys(handlersRef.current).map(event => {
      const cb = (msg) => handlersRef.current[event]?.(msg.data);
      channel.subscribe(event, cb);
      return { event, cb };
    });
    return () => subs.forEach(({ event, cb }) => channel.unsubscribe(event, cb));
  }, [channelName]);
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .gg-root {
    height: 100vh; width: 100vw; overflow: hidden;
    background: #060b1a;
    font-family: 'Barlow', sans-serif; color: #fff;
    display: flex; flex-direction: column;
    position: relative;
  }
  @keyframes ggFadeIn  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ggPop     { 0%{transform:scale(.8);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
  @keyframes ggPulse   { 0%,100%{opacity:.7} 50%{opacity:1} }
  @keyframes ggSpin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ggBounce  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes ggTimerBar{ from{width:100%} to{width:0%} }
  @keyframes ggRevealIn{
    0%{opacity:0;transform:scale(.94) translateY(20px)}
    60%{transform:scale(1.03) translateY(-4px)}
    100%{opacity:1;transform:scale(1) translateY(0)}
  }
  @keyframes confettiFall {
    0%   { transform: translateY(-10vh) rotate(0deg);   opacity: 1; }
    100% { transform: translateY(110vh)  rotate(720deg); opacity: 0; }
  }
  .gg-leaderboard-panel {
    position: fixed; top: 0; right: 0; height: 100vh; width: clamp(280px,26vw,460px);
    background: rgba(6,11,26,.92);
    border-left: 1px solid rgba(255,179,0,.15);
    transform: translateX(100%); transition: transform .4s cubic-bezier(.34,1.2,.64,1);
    z-index: 150; display: flex; flex-direction: column; padding: clamp(16px,2.5vh,24px) 0;
    backdrop-filter: blur(12px);
  }
  .gg-leaderboard-panel.open { transform: translateX(0); }
  .gg-score-row {
    display: flex; align-items: center; gap: clamp(8px,1vw,14px);
    padding: clamp(8px,1.1vh,13px) clamp(12px,1.6vw,22px); transition: background .2s;
  }
  .gg-score-row:hover { background: rgba(255,179,0,.06); }
`;

// Confetti is now the shared canvas component (imported above).

// ─── Sub-screens ──────────────────────────────────────────────────────────────
function WaitingScreen({ game }) {
  return (
    <div style={{
      flex: '1 0 auto', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 'clamp(12px,2.5vh,24px)',
      animation: 'ggFadeIn .7s ease both',
      padding: 'clamp(12px,2.5vh,32px) clamp(16px,3vw,48px)',
    }}>
      <div style={{ fontSize: 'clamp(40px,min(7vh,8vw),80px)', animation: 'ggBounce 2s ease infinite' }}>🗺️</div>
      <div style={{
        fontSize: 'clamp(24px,min(5vh,5vw),52px)', fontWeight: 900,
        fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 4,
        textShadow: '0 0 30px rgba(255,179,0,.6)',
      }}>{game?.name || 'GeoGuess'}</div>
      <div style={{
        fontSize: 'clamp(11px,min(1.6vh,1.4vw),16px)', letterSpacing: 4, color: 'rgba(255,179,0,.6)',
        fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
      }}>SCAVENGER HUNT — GAME STARTING SOON</div>
      <div style={{
        background: 'rgba(255,179,0,.08)', border: '1px solid rgba(255,179,0,.2)',
        borderRadius: 16, padding: 'clamp(12px,2vh,20px) clamp(20px,3.5vw,40px)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 'clamp(9px,1.1vw,13px)', letterSpacing: 4, color: 'rgba(255,179,0,.5)',
          fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 8 }}>GAME CODE</div>
        <div style={{ fontSize: 'clamp(28px,min(5.5vh,6vw),56px)', fontWeight: 900,
          fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 10, color: '#FFB300' }}>
          {game?.id || '——'}
        </div>
        <div style={{ fontSize: 'clamp(11px,min(1.4vh,1.2vw),14px)', color: 'rgba(255,255,255,.4)', marginTop: 4 }}>
          WhatsApp <strong>/join {game?.id}</strong> to participate
        </div>
      </div>
      <div style={{ animation: 'ggPulse 1.8s ease infinite', fontSize: 'clamp(11px,1.5vh,15px)',
        color: 'rgba(255,255,255,.4)', letterSpacing: 2 }}>
        Waiting for host to start…
      </div>
    </div>
  );
}

function CompletedScreen({ game, scores }) {
  const top4 = (scores || []).slice(0, 4);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 24,
      animation: 'ggFadeIn .7s ease both', position: 'relative' }}>
      <Confetti />
      <div style={{ fontSize: 'clamp(64px,7vw,120px)' }}>🏆</div>
      <div style={{ fontSize: 'clamp(32px,6vw,110px)', fontWeight: 900,
        fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 4,
        textShadow: '0 0 40px rgba(255,179,0,.8)' }}>
        Hunt Complete!
      </div>
      {game?.winner && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,179,0,.2), rgba(255,140,0,.1))',
          border: '1px solid rgba(255,179,0,.4)', borderRadius: 20,
          padding: '16px 40px', textAlign: 'center',
          animation: 'ggRevealIn .6s cubic-bezier(.34,1.2,.64,1) both',
        }}>
          <div style={{ fontSize: 'clamp(11px,1vw,18px)', letterSpacing: 4, color: 'rgba(255,179,0,.6)',
            fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 4 }}>WINNER</div>
          <div style={{ fontSize: 'clamp(24px,4vw,72px)', fontWeight: 900,
            fontFamily: "'Bebas Neue', sans-serif" }}>{game.winner.name}</div>
          <div style={{ fontSize: 'clamp(20px,2vw,38px)', color: '#FFB300', fontWeight: 700 }}>
            {game.winner.score} pts
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%',
        maxWidth: 'min(640px,84vw)', marginTop: 8 }}>
        {top4.map((s, i) => (
          <div key={s.jid || i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: i === 3 ? 'rgba(255,255,255,.03)' : 'rgba(255,255,255,.05)', borderRadius: 12, padding: 'clamp(10px,1.2vw,20px) clamp(16px,1.6vw,28px)',
          }}>
            <div style={{ fontSize: 'clamp(22px,2vw,40px)', minWidth: 28, textAlign: 'center', fontWeight: 800, color: 'rgba(255,255,255,.6)' }}>
              {['🥇','🥈','🥉'][i] || '4'}
            </div>
            <div style={{ flex: 1, fontWeight: 700, fontSize: 'clamp(16px,1.6vw,30px)' }}>{s.name}</div>
            <div style={{ color: '#FFB300', fontWeight: 800, fontSize: 'clamp(16px,1.6vw,30px)' }}>{s.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Active Game Screen ───────────────────────────────────────────────────────
function ActiveScreen({ round, roundIdx, totalRounds, solved, solveInfo, timerPct }) {
  const blurPx = solved ? 0 : (round?.blurLevel ?? 14);
  const zoom   = solved ? 1 : (round?.zoomLevel ?? 1.5);
  const cropX  = round?.cropX ?? 50;
  const cropY  = round?.cropY ?? 50;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>

      {/* Round counter strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 20px',
        background: 'rgba(0,0,0,.4)', borderBottom: '1px solid rgba(255,179,0,.1)',
      }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
          letterSpacing: 3, fontSize: 'clamp(13px,1.4vw,24px)', color: 'rgba(255,179,0,.7)' }}>
          ROUND {roundIdx + 1} / {totalRounds}
        </div>
        {solved && (
          <div style={{ fontSize: 'clamp(13px,1.4vw,24px)', fontWeight: 700, color: '#00D4AA',
            fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 2,
            animation: 'ggPop .4s ease both' }}>
            ✓ FOUND
          </div>
        )}
      </div>

      {/* Photo area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        {round?.imageUrl ? (
          <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <img
              src={`${SERVER_URL}${round.imageUrl}`}
              alt="location"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: `${cropX}% ${cropY}%`,
                transform: `scale(${zoom})`,
                transformOrigin: `${cropX}% ${cropY}%`,
                filter: `blur(${blurPx}px)`,
                transition: 'filter 1s ease, transform 1s ease',
                display: 'block',
              }}
            />
          </div>
        ) : (
          <div style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,.03)',
          }}>
            <div style={{ fontSize: 80, opacity: .3 }}>📷</div>
          </div>
        )}

        {/* Solved winner overlay */}
        {solved && solveInfo && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.7) 100%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-end',
            padding: 32,
          }}>
            <div style={{
              background: 'rgba(0,212,170,.15)', border: '2px solid rgba(0,212,170,.5)',
              borderRadius: 20, padding: '20px 40px', textAlign: 'center',
              animation: 'ggRevealIn .5s cubic-bezier(.34,1.2,.64,1) both',
            }}>
              <div style={{ fontSize: 'clamp(11px,1vw,18px)', letterSpacing: 4, color: 'rgba(0,212,170,.8)',
                fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 4 }}>
                FOUND BY
              </div>
              <div style={{ fontSize: 'clamp(22px,3.6vw,60px)', fontWeight: 900,
                fontFamily: "'Bebas Neue', sans-serif" }}>{solveInfo.winnerName}</div>
              <div style={{ fontSize: 'clamp(18px,1.8vw,32px)', color: '#FFB300', fontWeight: 700, marginTop: 4 }}>
                +{solveInfo.pointsAwarded} pts
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hint bar */}
      <div style={{
        padding: '14px 24px',
        background: 'rgba(0,0,0,.6)', borderTop: '1px solid rgba(255,179,0,.1)',
      }}>
        <div style={{ fontSize: 'clamp(11px,1vw,18px)', letterSpacing: 4, color: 'rgba(255,179,0,.5)',
          fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 4 }}>HINT</div>
        <div style={{ fontSize: 'clamp(16px,2.6vw,40px)', fontWeight: 700, lineHeight: 1.3 }}>
          {round?.hint || 'Find the hidden code…'}
        </div>
      </div>

      {/* Timer bar */}
      {(round?.timeLimit || 0) > 0 && (
        <div style={{ height: 4, background: 'rgba(255,255,255,.1)', flexShrink: 0 }}>
          <div style={{
            height: '100%',
            width: `${timerPct * 100}%`,
            background: timerPct > 0.5 ? '#00D4AA' : timerPct > 0.25 ? '#FFB300' : '#FF4B4B',
            transition: 'width .5s linear, background .5s linear',
          }} />
        </div>
      )}
    </div>
  );
}

// ─── Leaderboard Panel ────────────────────────────────────────────────────────
function LeaderboardPanel({ scores, open, onClose }) {
  return (
    <div className={`gg-leaderboard-panel${open ? ' open' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px 16px', borderBottom: '1px solid rgba(255,179,0,.1)' }}>
        <div style={{ fontSize: 'clamp(13px,1.3vw,22px)', fontWeight: 700, letterSpacing: 3,
          fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(255,179,0,.7)' }}>
          LEADERBOARD
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,.4)',
            cursor: 'pointer', fontSize: 'clamp(16px,1.8vw,22px)', lineHeight: 1,
            minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
        {(scores || []).length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center',
            color: 'rgba(255,255,255,.25)', fontSize: 13 }}>
            No scores yet
          </div>
        ) : (scores || []).map((s, i) => (
          <div key={s.jid || i} className="gg-score-row">
            <div style={{
              width: 'clamp(28px,2.4vw,44px)', height: 'clamp(28px,2.4vw,44px)', borderRadius: 8,
              background: i === 0 ? 'linear-gradient(135deg,#FFB300,#FF8C00)' :
                          i === 1 ? 'rgba(192,192,192,.2)' :
                          i === 2 ? 'rgba(205,127,50,.2)' : 'rgba(255,255,255,.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: i < 3 ? 'clamp(14px,1.3vw,22px)' : 'clamp(11px,1.1vw,18px)', fontWeight: 900,
              color: i === 0 ? '#1a0a00' : 'rgba(255,255,255,.7)',
              flexShrink: 0,
            }}>
              {i < 3 ? ['🥇','🥈','🥉'][i] : i + 1}
            </div>
            <div style={{ flex: 1, fontWeight: 600, fontSize: 'clamp(13px,1.3vw,24px)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.name}
            </div>
            <div style={{ fontWeight: 800, fontSize: 'clamp(13px,1.4vw,26px)', color: '#FFB300', flexShrink: 0 }}>
              {s.score}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GeoGuessBoard({ gameId, onExit, onGameCompleted, tvMode }) {
  const [game,       setGame]       = useState(null);
  const [round,      setRound]      = useState(null);
  const [roundIdx,   setRoundIdx]   = useState(-1);
  const [totalRounds,setTotal]      = useState(0);
  const [scores,     setScores]     = useState([]);
  const [solveInfo,  setSolveInfo]  = useState(null);
  const [solved,     setSolved]     = useState(false);
  const [timerPct,   setTimerPct]   = useState(1);
  const [lbOpen,     setLbOpen]     = useState(false);
  const [loading,    setLoading]    = useState(true);

  const timerRef   = useRef(null);
  const lbTimerRef = useRef(null);

  // ── Timer tick — declared first so the initial-load effect can reference it ──
  const startTimerTick = useCallback((timeLimit, startedAt) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!timeLimit || timeLimit <= 0) { setTimerPct(1); return; }
    const tick = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const pct     = Math.max(0, 1 - elapsed / timeLimit);
      setTimerPct(pct);
      if (pct <= 0) clearInterval(timerRef.current);
    };
    tick();
    timerRef.current = setInterval(tick, 500);
  }, []);

  // ── Leaderboard auto-show ───────────────────────────────────────────────────
  const showLeaderboard = useCallback((duration = 6000) => {
    setLbOpen(true);
    if (lbTimerRef.current) clearTimeout(lbTimerRef.current);
    lbTimerRef.current = setTimeout(() => setLbOpen(false), duration);
  }, []);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetch(`${API}/geoguess/${gameId}`).then(r => r.json());
        if (cancelled) return;
        setGame(data);
        setScores(Object.values(data.scores || {}).sort((a, b) => b.score - a.score));

        // If already active, hydrate the current round directly — the Ably round-changed
        // event was published before this board subscribed so we can't wait for it.
        if (data.status === 'active' && data.currentRound >= 0 && data.locationSetId) {
          try {
            const setData = await fetch(`${API}/location-sets/${data.locationSetId}`).then(r => r.json());
            const r = setData.rounds?.[data.currentRound];
            if (r && !cancelled) {
              setRound({
                hint:       r.hint       || '',
                imageUrl:   r.imageUrl   || '',
                blurLevel:  r.blurLevel  ?? 14,
                zoomLevel:  r.zoomLevel  ?? 1.5,
                cropX:      r.cropX      ?? 50,
                cropY:      r.cropY      ?? 50,
                timeLimit:  r.timeLimit  ?? 0,
                basePoints: r.basePoints ?? 500,
              });
              setRoundIdx(data.currentRound);
              setTotal(setData.rounds.length);
              if (data.solvedRounds?.[data.currentRound]) {
                setSolved(true);
                setSolveInfo(data.solvedRounds[data.currentRound]);
              } else {
                startTimerTick(r.timeLimit, data.roundStartedAt);
              }
            }
          } catch {}
        }
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [gameId, startTimerTick]);

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(lbTimerRef.current); }, []);

  // ── Detect deletion on standalone display (TVDisplay handles it itself) ────
  useAblyChannel('crossword:admin', {
    'geoguess-deleted': (data) => {
      if (data.gameId === gameId && onExit) onExit();
    },
  });

  // ── Ably event handlers ──────────────────────────────────────────────────────
  useAblyChannel(`geoguess:game:${gameId}`, {
    'game-state': (data) => {
      setGame(g => ({ ...(g || {}), status: data.state }));
      if (data.state === 'active') {
        // round-changed fires server-side at the same moment as game-state:active,
        // so it may arrive before or after — or may have been missed entirely if
        // the board was just mounting.  Re-fetch the game + location set to hydrate
        // the current round reliably.
        (async () => {
          try {
            const gameData = await fetch(`${API}/geoguess/${gameId}`).then(r => r.json());
            if (gameData.status === 'active' && gameData.currentRound >= 0 && gameData.locationSetId) {
              const setData = await fetch(`${API}/location-sets/${gameData.locationSetId}`).then(r => r.json());
              const r = setData.rounds?.[gameData.currentRound];
              if (r) {
                setGame(g => ({ ...(g || {}), ...gameData }));
                setRound({
                  hint:       r.hint       || '',
                  imageUrl:   r.imageUrl   || '',
                  blurLevel:  r.blurLevel  ?? 14,
                  zoomLevel:  r.zoomLevel  ?? 1.5,
                  cropX:      r.cropX      ?? 50,
                  cropY:      r.cropY      ?? 50,
                  timeLimit:  r.timeLimit  ?? 0,
                  basePoints: r.basePoints ?? 500,
                });
                setRoundIdx(gameData.currentRound);
                setTotal(setData.rounds.length);
                setSolved(false);
                setSolveInfo(null);
                setTimerPct(1);
                startTimerTick(r.timeLimit, gameData.roundStartedAt);
              }
            }
          } catch {}
        })();
      }
      if (data.state === 'waiting') {
        // Game reset to waiting — clear any active round state
        setRound(null);
        setRoundIdx(-1);
        setSolved(false);
        setSolveInfo(null);
        if (timerRef.current) clearInterval(timerRef.current);
      }
      if (data.state === 'completed') {
        setScores(data.scores || []);
        if (data.winner) setGame(g => ({ ...(g || {}), winner: data.winner }));
        if (timerRef.current) clearInterval(timerRef.current);
        if (onGameCompleted) onGameCompleted(gameId, 'geoguess');
      }
    },
    'round-changed': (data) => {
      setRound({
        hint:       data.hint,
        imageUrl:   data.imageUrl,
        blurLevel:  data.blurLevel,
        zoomLevel:  data.zoomLevel,
        cropX:      data.cropX,
        cropY:      data.cropY,
        timeLimit:  data.timeLimit,
        basePoints: data.basePoints,
      });
      setRoundIdx(data.roundIdx);
      setTotal(data.total);
      setSolved(false);
      setSolveInfo(null);
      setTimerPct(1);
      startTimerTick(data.timeLimit, data.ts);
    },
    'round-solved': (data) => {
      setSolved(true);
      setSolveInfo(data);
      setScores(data.scores || []);
      if (timerRef.current) clearInterval(timerRef.current);
      showLeaderboard(5000);
    },
    'round-revealed': () => {
      setSolved(true);
      setSolveInfo(si => si || { winnerName: 'Nobody', pointsAwarded: 0 });
      if (timerRef.current) clearInterval(timerRef.current);
    },
    'score-update': (data) => {
      setScores(data.scores || []);
    },
  });

  if (loading) {
    return (
      <div className="gg-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <style>{STYLES}</style>
        <div style={{ animation: 'ggSpin 1s linear infinite', fontSize: 40 }}>⚙️</div>
      </div>
    );
  }

  const status = game?.status || 'waiting';

  return (
    <div className="gg-root">
      <style>{STYLES}</style>

      {/* Fade-to-black gradient background */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 30% 20%, rgba(60,30,120,.25) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(0,80,60,.15) 0%, transparent 60%)',
      }} />

      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 10, flex: 1, display: 'flex',
        flexDirection: 'column', minHeight: 0,
        overflowX: 'hidden', overflowY: status === 'waiting' || status === 'completed' ? 'auto' : 'hidden' }}>

        {status === 'waiting' && <WaitingScreen game={game} />}

        {status === 'active' && round && (
          <ActiveScreen
            round={round}
            roundIdx={roundIdx}
            totalRounds={totalRounds}
            solved={solved}
            solveInfo={solveInfo}
            timerPct={timerPct}
          />
        )}

        {status === 'active' && !round && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ animation: 'ggPulse 1.5s ease infinite', fontSize: 13,
              color: 'rgba(255,255,255,.4)', letterSpacing: 2 }}>Loading round…</div>
          </div>
        )}

        {status === 'completed' && <CompletedScreen game={game} scores={scores} />}
      </div>

      {/* Leaderboard panel */}
      <LeaderboardPanel scores={scores} open={lbOpen} onClose={() => setLbOpen(false)} />

      {/* Nav bar — scores toggle + exit */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(6px,1vh,12px) clamp(10px,1.5vw,18px)',
        background: 'rgba(0,0,0,.5)', borderTop: '1px solid rgba(255,255,255,.05)',
        backdropFilter: 'blur(8px)',
      }}>
        <button
          onClick={() => setLbOpen(o => !o)}
          style={{
            background: 'rgba(255,179,0,.1)', border: '1px solid rgba(255,179,0,.3)',
            borderRadius: 8, color: '#FFB300', fontSize: 'clamp(11px,1.1vw,14px)', fontWeight: 700,
            fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 3,
            padding: 'clamp(10px,1.3vh,14px) clamp(12px,1.5vw,20px)', cursor: 'pointer',
            minHeight: 44,
          }}>
          {lbOpen ? 'HIDE' : '📊 SCORES'}
        </button>

        {!tvMode && onExit && (
          <button
            onClick={onExit}
            style={{
              background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 8, color: 'rgba(255,255,255,.4)', fontSize: 'clamp(11px,1.1vw,14px)', fontWeight: 700,
              fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 3,
              padding: 'clamp(10px,1.3vh,14px) clamp(12px,1.5vw,20px)', cursor: 'pointer',
              minHeight: 44,
            }}>
            ← EXIT
          </button>
        )}
      </div>
    </div>
  );
}
